import type { GameState, LogEntry } from './types';
import { PROCRASTINATIONS, DETOURS, WISDOM_UPGRADES, getProcrastinationCost, getDetourCost, getWisdomUpgradeCost } from './upgrades';
import { calculatePassiveRate, calculateClickPower } from './game-state';
import { MIN_PRESTIGE_TIME, calculateWisdomGain } from './prestige';

// Audio Context
let audioCtx: AudioContext | null = null;
let isMuted = localStorage.getItem('wasting_time_muted') === 'true';

function getAudioContext(): AudioContext | null {
  if (isMuted) return null;
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

export function initMuteButtonState(): void {
  const btn = document.getElementById('sound-toggle');
  if (btn) {
    btn.innerHTML = `<span class="icon">${isMuted ? '🔇' : '🔊'}</span>`;
  }
}

export function toggleSound(): boolean {
  isMuted = !isMuted;
  localStorage.setItem('wasting_time_muted', isMuted ? 'true' : 'false');
  initMuteButtonState();
  if (isMuted && audioCtx) {
    audioCtx.close().then(() => {
      audioCtx = null;
    });
  }
  return isMuted;
}

export function playTickSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.03);
  
  gain.gain.setValueAtTime(0.015, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.03);
  
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.04);
}

export function playClickSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(280, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.08);
  
  gain.gain.setValueAtTime(0.06, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
  
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.09);
}

export function playUpgradeSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = 'sine';
  osc.frequency.setValueAtTime(320, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(640, ctx.currentTime + 0.12);
  
  gain.gain.setValueAtTime(0.05, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
  
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.13);
}

export function playPrestigeSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const now = ctx.currentTime;
  const notes = [261.63, 329.63, 392.00, 523.25, 659.25]; // C major 7 chord
  
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + i * 0.07);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.03, now + i * 0.07 + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.07 + 1.4);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + i * 0.07);
    osc.stop(now + i * 0.07 + 1.5);
  });
}

// Formatting Utilities
export function formatTime(seconds: number, showMs = false): string {
  if (seconds < 0) seconds = 0;
  const years = Math.floor(seconds / (365 * 86400));
  seconds %= (365 * 86400);
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  const wholeSeconds = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);

  const pad = (n: number) => n.toString().padStart(2, '0');
  
  let timeStr = '';
  if (years > 0) timeStr += `${years}y `;
  if (days > 0 || years > 0) timeStr += `${days}d `;
  
  if (years > 0 || days > 0) {
    return `${timeStr}${hours}h ${minutes}m ${wholeSeconds}s`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${wholeSeconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${wholeSeconds}s`;
  }
  return showMs ? `${wholeSeconds}.${pad(ms)}s` : `${wholeSeconds}s`;
}

// Particle Structures
interface SandParticle {
  element: SVGCircleElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

let particles: SandParticle[] = [];

export function updateHourglassSandParticles(sandLevelRatio: number, isFlowing: boolean, deltaTime: number, isFlipped = false): void {
  const svg = document.getElementById('hourglass-svg') as unknown as SVGSVGElement | null;
  if (!svg) return;
  
  let group = document.getElementById('sand-particles') as unknown as SVGGElement | null;
  if (!group) {
    group = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    group.setAttribute('id', 'sand-particles');
    svg.appendChild(group);
  }
  
  const currentTargetY = isFlipped 
    ? (20 + 130 * (1 - sandLevelRatio))
    : (150 + 130 * sandLevelRatio);
  const stream = document.getElementById('sand-stream');
  
  if (stream) {
    if (isFlowing && sandLevelRatio < 1.0) {
      stream.setAttribute('opacity', '0.6');
      stream.setAttribute('y1', '150');
      stream.setAttribute('y2', currentTargetY.toString());
      const currentOffset = parseFloat(stream.getAttribute('stroke-dashoffset') || '0');
      const speed = isFlipped ? -20 : 20;
      let nextOffset = (currentOffset + speed * deltaTime) % 12;
      if (nextOffset < 0) nextOffset += 12;
      stream.setAttribute('stroke-dashoffset', nextOffset.toString());
      
      // Spawn particles
      if (Math.random() < 0.6) {
        const pEl = document.createElementNS('http://www.w3.org/2000/svg', 'circle') as SVGCircleElement;
        pEl.setAttribute('r', (1.0 + Math.random() * 1.5).toFixed(1));
        pEl.setAttribute('fill', 'url(#sand-grad)');
        pEl.setAttribute('opacity', (0.4 + Math.random() * 0.6).toFixed(2));
        group.appendChild(pEl);
        
        const spawnY = isFlipped ? 153 : 147;
        const initVy = isFlipped 
          ? -(110 + Math.random() * 30)
          : (110 + Math.random() * 30);
        
        particles.push({
          element: pEl,
          x: 100 + (Math.random() * 4 - 2),
          y: spawnY,
          vx: Math.random() * 12 - 6,
          vy: initVy,
          life: 1.0
        });
      }
    } else {
      stream.setAttribute('opacity', '0');
    }
  }
  
  // Update particles
  const nextParticles: SandParticle[] = [];
  const gravity = isFlipped ? -98 : 98;
  
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    p.y += p.vy * deltaTime;
    p.x += p.vx * deltaTime;
    p.vy += gravity * deltaTime; // gravity
    
    const isPastTarget = isFlipped ? (p.y <= currentTargetY) : (p.y >= currentTargetY);
    
    if (isPastTarget) {
      // Chance of a tiny bounce splash
      if (p.life > 0.4 && Math.random() < 0.25) {
        p.y = currentTargetY;
        p.vy = isFlipped 
          ? (Math.random() * 18 + 8)
          : (-Math.random() * 18 - 8);
        p.vx = Math.random() * 24 - 12;
        p.life -= 0.35;
        p.element.setAttribute('cx', p.x.toFixed(1));
        p.element.setAttribute('cy', p.y.toFixed(1));
        nextParticles.push(p);
      } else {
        p.element.remove();
      }
    } else {
      p.element.setAttribute('cx', p.x.toFixed(1));
      p.element.setAttribute('cy', p.y.toFixed(1));
      nextParticles.push(p);
    }
  }
  particles = nextParticles;
}

export function drawHourglassSand(sandLevelRatio: number, isFlipped = false): void {
  const topClipRect = document.getElementById('top-sand-clip-rect');
  const bottomClipRect = document.getElementById('bottom-sand-clip-rect');
  
  if (topClipRect) {
    if (!isFlipped) {
      const h = 130 * sandLevelRatio;
      const y = 20 + 130 * (1 - sandLevelRatio);
      topClipRect.setAttribute('y', y.toString());
      topClipRect.setAttribute('height', h.toString());
    } else {
      const h = 130 * (1 - sandLevelRatio);
      const y = 20;
      topClipRect.setAttribute('y', y.toString());
      topClipRect.setAttribute('height', h.toString());
    }
  }
  
  if (bottomClipRect) {
    if (!isFlipped) {
      const h = 130 * (1 - sandLevelRatio);
      const y = 280 - h;
      bottomClipRect.setAttribute('y', y.toString());
      bottomClipRect.setAttribute('height', h.toString());
    } else {
      const h = 130 * sandLevelRatio;
      const y = 150;
      bottomClipRect.setAttribute('y', y.toString());
      bottomClipRect.setAttribute('height', h.toString());
    }
  }
}

// Floating Click Texts
export function spawnFloatingClickText(amount: number, x: number, y: number): void {
  const wrapper = document.querySelector('.hourglass-wrapper');
  if (!wrapper) return;
  
  const span = document.createElement('span');
  span.className = 'floating-text';
  span.textContent = `+${amount.toFixed(2)}s`;
  
  // Position relative to wrapper bounding rect
  const rect = wrapper.getBoundingClientRect();
  const left = x - rect.left;
  const top = y - rect.top;
  
  span.style.left = `${left}px`;
  span.style.top = `${top}px`;
  
  wrapper.appendChild(span);
  
  // Remove after animation completes
  setTimeout(() => {
    span.remove();
  }, 1000);
}

// Tab navigation handler
export function setupTabs(): void {
  const tabButtons = document.querySelectorAll('.tab-button');
  const tabContents = document.querySelectorAll('.tab-content');
  
  tabButtons.forEach(button => {
    button.addEventListener('click', () => {
      const targetTab = button.getAttribute('data-tab');
      if (!targetTab) return;
      
      tabButtons.forEach(btn => btn.classList.remove('active'));
      tabContents.forEach(content => content.classList.remove('active'));
      
      button.classList.add('active');
      const targetEl = document.getElementById(`tab-${targetTab}`);
      if (targetEl) targetEl.classList.add('active');
    });
  });
}

// Activity Log console
let logsList: LogEntry[] = [];
const MAX_LOGS = 50;

export function addLogEntry(message: string, type: LogEntry['type'] = 'default'): void {
  const date = new Date();
  const timestamp = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  
  const log: LogEntry = {
    id: `${Date.now()}-${Math.random()}`,
    timestamp,
    message,
    type
  };
  
  logsList.push(log);
  if (logsList.length > MAX_LOGS) {
    logsList.shift();
  }
  
  renderLogs();
}

function renderLogs(): void {
  const feed = document.getElementById('console-feed');
  if (!feed) return;
  
  feed.innerHTML = '';
  logsList.forEach(log => {
    const div = document.createElement('div');
    div.className = `log-line log-${log.type}`;
    div.innerHTML = `<span class="log-time">[${log.timestamp}]</span> <span class="log-message">${log.message}</span>`;
    feed.appendChild(div);
  });
  
  // Scroll to bottom
  feed.scrollTop = feed.scrollHeight;
}

// Game Loop Render UI
export function renderUI(state: GameState): void {
  // Main numerical countdown values
  const hours = Math.floor(state.timeWasted / 3600);
  const minutes = Math.floor((state.timeWasted % 3600) / 60);
  const seconds = Math.floor(state.timeWasted % 60);
  const ms = Math.floor((state.timeWasted % 1) * 100);
  
  const pad = (n: number) => n.toString().padStart(2, '0');
  
  const hrEl = document.getElementById('time-hours');
  const minEl = document.getElementById('time-minutes');
  const secEl = document.getElementById('time-seconds');
  const msEl = document.getElementById('time-ms');
  
  if (hrEl) hrEl.textContent = pad(hours);
  if (minEl) minEl.textContent = pad(minutes);
  if (secEl) secEl.textContent = pad(seconds);
  if (msEl) msEl.textContent = pad(ms);
  
  // Rates and labels
  const rate = calculatePassiveRate(state);
  const rateDisplay = document.getElementById('passive-rate-display');
  if (rateDisplay) {
    // If sand is empty, indicate passive rate reduction
    if (state.sandAmount <= 0 && rate > 0) {
      rateDisplay.textContent = `wasting ${rate.toFixed(1)}s / sec (10% speed: sand is empty!)`;
      rateDisplay.classList.add('warning-text');
    } else {
      rateDisplay.textContent = `wasting ${rate.toFixed(1)}s / second passively`;
      rateDisplay.classList.remove('warning-text');
    }
  }
  
  const clickPower = calculateClickPower(state);
  const clickPowerDisplay = document.getElementById('click-power-display');
  if (clickPowerDisplay) {
    clickPowerDisplay.textContent = `Click hourglass to waste ${clickPower.toFixed(2)}s`;
  }
  
  // Top header indicators
  const epCount = document.getElementById('epiphanies-count');
  if (epCount) epCount.textContent = state.epiphaniesCount.toString();
  
  const wisCount = document.getElementById('wisdom-count');
  if (wisCount) wisCount.textContent = state.wisdom.toString();
  
  // Stats block
  const statClicks = document.getElementById('stat-clicks');
  if (statClicks) statClicks.textContent = state.clicks.toString();
  
  const statActive = document.getElementById('stat-active-time');
  if (statActive) statActive.textContent = formatTime(state.stats.timeSpentActive);
  
  const statOffline = document.getElementById('stat-offline-time');
  if (statOffline) statOffline.textContent = formatTime(state.stats.totalOfflineTimeWasted);
  
  const statLifetime = document.getElementById('stat-lifetime-time');
  if (statLifetime) statLifetime.textContent = formatTime(state.lifetimeTimeWasted);
  
  // Prestige Section
  const prestigeBanner = document.getElementById('prestige-banner');
  const prestigeWisGain = document.getElementById('prestige-wisdom-gain');
  
  if (prestigeBanner && prestigeWisGain) {
    const wisdomGain = calculateWisdomGain(state);
    if (state.totalTimeWasted >= MIN_PRESTIGE_TIME) {
      prestigeBanner.classList.remove('hidden');
      prestigeWisGain.textContent = `+${wisdomGain} Wisdom`;
    } else {
      prestigeBanner.classList.add('hidden');
    }
  }

  // Shop lists
  renderShopLists(state);
}

// Render Shop Catalog Tables
let lastProcrastinationsHash = '';
let lastDetoursHash = '';
let lastWisdomHash = '';

function renderShopLists(state: GameState): void {
  // Procrastinations List
  const procList = document.getElementById('procrastinations-list');
  if (procList) {
    // Generate simple hash representation to avoid rebuilding DOM every frame unless needed
    const costReductionLevel = state.wisdomUpgrades['idle_stupor'] || 0;
    const values = PROCRASTINATIONS.map(p => {
      const count = state.procrastinations[p.id] || 0;
      const cost = getProcrastinationCost(p, count, costReductionLevel);
      const affordable = state.timeWasted >= cost;
      return `${p.id}:${count}:${affordable}`;
    }).join(',');
    
    if (values !== lastProcrastinationsHash) {
      lastProcrastinationsHash = values;
      procList.innerHTML = '';
      
      PROCRASTINATIONS.forEach(p => {
        const count = state.procrastinations[p.id] || 0;
        const cost = getProcrastinationCost(p, count, costReductionLevel);
        const card = document.createElement('div');
        card.className = `upgrade-card ${state.timeWasted < cost ? 'disabled' : ''}`;
        
        card.innerHTML = `
          <div class="upgrade-info">
            <div class="upgrade-name">${p.name} <span class="upgrade-count">x${count}</span></div>
            <div class="upgrade-desc">${p.description}</div>
            <div class="upgrade-effect">+${p.baseRate.toFixed(1)}s/sec passive</div>
          </div>
          <button class="btn btn-buy" data-id="${p.id}" ${state.timeWasted < cost ? 'disabled' : ''}>
            ${formatTime(cost)}
          </button>
        `;
        procList.appendChild(card);
      });
    }
  }
  
  // Click Detours List
  const detourList = document.getElementById('detours-list');
  if (detourList) {
    const values = DETOURS.map(d => {
      const count = state.detours[d.id] || 0;
      const cost = getDetourCost(d, count);
      const affordable = state.timeWasted >= cost;
      return `${d.id}:${count}:${affordable}`;
    }).join(',');
    
    if (values !== lastDetoursHash) {
      lastDetoursHash = values;
      detourList.innerHTML = '';
      
      DETOURS.forEach(d => {
        const count = state.detours[d.id] || 0;
        const cost = getDetourCost(d, count);
        const card = document.createElement('div');
        card.className = `upgrade-card ${state.timeWasted < cost ? 'disabled' : ''}`;
        
        card.innerHTML = `
          <div class="upgrade-info">
            <div class="upgrade-name">${d.name} <span class="upgrade-count">x${count}</span></div>
            <div class="upgrade-desc">${d.description}</div>
            <div class="upgrade-effect">+${d.baseIncrement.toFixed(1)}s/click active</div>
          </div>
          <button class="btn btn-buy-detour" data-id="${d.id}" ${state.timeWasted < cost ? 'disabled' : ''}>
            ${formatTime(cost)}
          </button>
        `;
        detourList.appendChild(card);
      });
    }
  }
  
  // Wisdom Upgrades List
  const wisdomList = document.getElementById('wisdom-list');
  if (wisdomList) {
    const values = WISDOM_UPGRADES.map(w => {
      const lvl = state.wisdomUpgrades[w.id] || 0;
      const cost = getWisdomUpgradeCost(w, lvl);
      const maxed = lvl >= w.maxLevel;
      const affordable = state.wisdom >= cost && !maxed;
      return `${w.id}:${lvl}:${affordable}:${maxed}`;
    }).join(',');
    
    if (values !== lastWisdomHash) {
      lastWisdomHash = values;
      wisdomList.innerHTML = '';
      
      WISDOM_UPGRADES.forEach(w => {
        const lvl = state.wisdomUpgrades[w.id] || 0;
        const cost = getWisdomUpgradeCost(w, lvl);
        const maxed = lvl >= w.maxLevel;
        const card = document.createElement('div');
        card.className = `upgrade-card wisdom-card ${state.wisdom < cost && !maxed ? 'disabled' : ''} ${maxed ? 'maxed' : ''}`;
        
        card.innerHTML = `
          <div class="upgrade-info">
            <div class="upgrade-name">${w.name} <span class="upgrade-count">Lvl ${lvl}/${w.maxLevel}</span></div>
            <div class="upgrade-desc">${w.description}</div>
          </div>
          <button class="btn btn-buy-wisdom" data-id="${w.id}" ${(state.wisdom < cost || maxed) ? 'disabled' : ''}>
            ${maxed ? 'MAXED' : `${cost} Wisdom`}
          </button>
        `;
        wisdomList.appendChild(card);
      });
    }
  }
}

// Show Offline Progress Modal Dialog
export function showOfflineModal(elapsedSeconds: number, wastedOffline: number): void {
  const modal = document.getElementById('offline-modal');
  const elapsedEl = document.getElementById('offline-time-gone');
  const wastedEl = document.getElementById('offline-wasted');
  const flavorEl = document.getElementById('offline-flavor');
  
  if (modal && elapsedEl && wastedEl) {
    elapsedEl.textContent = formatTime(elapsedSeconds);
    wastedEl.textContent = formatTime(wastedOffline);
    
    const FLAVORS = [
      `"Procrastination is the art of keeping up with yesterday."`,
      `"Time you enjoy wasting is not wasted time."`,
      `"The trouble is, you think you have time."`,
      `"Never put off till tomorrow what you can do the day after tomorrow."`,
      `"Doing nothing is very hard to do... you never know when you're finished."`,
      `"My tomorrow is already fully booked with procrastination."`
    ];
    if (flavorEl) {
      flavorEl.textContent = FLAVORS[Math.floor(Math.random() * FLAVORS.length)];
    }
    
    modal.classList.remove('hidden');
  }
}

export function setupOfflineModalClose(): void {
  const closeBtn = document.getElementById('close-offline-modal');
  const modal = document.getElementById('offline-modal');
  if (closeBtn && modal) {
    closeBtn.addEventListener('click', () => {
      // Resume browser audio capability upon first interaction
      getAudioContext();
      modal.classList.add('hidden');
    });
  }
}

export function showEndGameModal(state: GameState): void {
  const modal = document.getElementById('end-game-modal');
  const clicksEl = document.getElementById('end-clicks');
  const epEl = document.getElementById('end-epiphanies');
  const rtEl = document.getElementById('end-real-time');
  const ltEl = document.getElementById('end-lifetime-wasted');
  const flavorEl = document.getElementById('end-flavor');
  
  if (modal && clicksEl && epEl && rtEl && ltEl) {
    clicksEl.textContent = state.clicks.toString();
    epEl.textContent = state.epiphaniesCount.toString();
    rtEl.textContent = formatTime(state.stats.timeSpentActive);
    ltEl.textContent = formatTime(state.lifetimeTimeWasted);
    
    const FLAVORS = [
      `"Congratulations. You did it. Are you going to put this on your resume?"`,
      `"If only you could channel this level of dedication into something that actually matters."`,
      `"You spent real-world minutes to maximize virtual sand flow. Let that sink in."`,
      `"Bertrand Russell once said 'Time you enjoy wasting is not wasted.' We think he might make an exception for this."`,
      `"The heat death of the universe is closer now. Good job accelerating it."`,
      `"You have successfully avoided checking your emails, folding your laundry, and general adulthood. Victory!"`
    ];
    if (flavorEl) {
      flavorEl.textContent = FLAVORS[Math.floor(Math.random() * FLAVORS.length)];
    }
    
    modal.classList.remove('hidden');
  }
}

export function hideEndGameModal(): void {
  const modal = document.getElementById('end-game-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
}
